import { habitPausedOn, parseDateKey, shiftDateKey } from './habit-schedule';
import { todayIn } from '../utils/date-utils';
export function dormantProjectIds(projects) {
    const dormant = new Map();
    for (const project of projects) {
        if (project.status === 'paused' || project.status === 'archived'
            || project.status === 'completed') {
            dormant.set(project.id, project.status);
        }
    }
    const childrenOf = new Map();
    for (const project of projects) {
        if (!project.parent_id)
            continue;
        const siblings = childrenOf.get(project.parent_id) ?? [];
        siblings.push(project);
        childrenOf.set(project.parent_id, siblings);
    }
    const queue = [...dormant.keys()];
    const seen = new Set();
    while (queue.length > 0) {
        const id = queue.pop();
        if (seen.has(id))
            continue;
        seen.add(id);
        const reason = dormant.get(id);
        for (const child of childrenOf.get(id) ?? []) {
            if (!dormant.has(child.id)) {
                // A child with no status of its own inherits the ancestor's reason,
                // so the explanation names something the user actually did.
                dormant.set(child.id, reason);
                queue.push(child.id);
            }
        }
    }
    return dormant;
}
/**
 * Just the paused ones.
 *
 * Kept because "paused" is a distinct thing to say and to filter on, but
 * derived from the one walk above rather than repeating it — two tree walks
 * is two chances to disagree about what inheritance reaches.
 */
export function pausedProjectIds(projects) {
    const dormant = dormantProjectIds(projects);
    return new Set([...dormant].filter(([, reason]) => reason === 'paused').map(([id]) => id));
}
/** The reason one task isn't scheduled, with words for it. */
export function unscheduledReason(task, { tasks, projects, dormantProjects, preferences, today = todayIn() }) {
    const minDuration = preferences?.task_defaults?.min_duration ?? 15;
    const maxContinuous = preferences?.scheduling_preferences?.max_deep_work_stretch ?? 120;
    if (habitPausedOn(task, today)) {
        const until = task.paused_until ? parseDateKey(task.paused_until.slice(0, 10)) : null;
        return {
            taskId: task.id,
            reason: 'paused',
            details: until
                ? `Paused until ${until.toLocaleDateString()}`
                : 'Task is paused',
        };
    }
    const dormant = dormantProjects.get(task.project_id);
    if (dormant) {
        const project = projects.find((p) => p.id === task.project_id);
        const named = project ? `Project "${project.name}"` : 'Project';
        return {
            taskId: task.id,
            reason: dormant === 'paused' ? 'project_paused'
                : dormant === 'archived' ? 'project_archived' : 'project_completed',
            details: `${named} is ${dormant}`,
        };
    }
    // Only regular tasks are filtered by min_duration — habits can be short.
    if (!task.is_habit && task.estimated_minutes < minDuration) {
        return {
            taskId: task.id,
            reason: 'too_short',
            details: `${task.estimated_minutes}min < ${minDuration}min min`,
        };
    }
    if (task.estimated_minutes > maxContinuous) {
        return {
            taskId: task.id,
            reason: 'too_long',
            details: `${task.estimated_minutes}min > ${maxContinuous}min max`,
        };
    }
    if (task.dependencies && task.dependencies.length > 0) {
        const byId = new Map(tasks.map((t) => [t.id, t]));
        const unmet = task.dependencies.filter((id) => {
            const dependency = byId.get(id);
            return dependency && dependency.status !== 'completed';
        });
        if (unmet.length > 0) {
            const names = unmet.map((id) => byId.get(id)?.title).filter(Boolean).slice(0, 2);
            return {
                taskId: task.id,
                reason: 'dependencies_unmet',
                details: names.length > 0
                    ? `Waiting for: ${names.join(', ')}${unmet.length > 2 ? ` +${unmet.length - 2} more` : ''}`
                    : `${unmet.length} unmet dependencies`,
            };
        }
    }
    if (task.is_habit) {
        return {
            taskId: task.id,
            reason: 'habit_no_occurrence',
            details: 'No occurrence in scheduled period',
        };
    }
    return {
        taskId: task.id,
        reason: 'no_available_slots',
        details: 'No available time slots fit this task',
    };
}
/** Every open task that isn't on the plan, with its reason. */
export function unscheduledTasks(scheduledTaskIds, context) {
    return context.tasks
        .filter((task) => task.status !== 'completed' && !scheduledTaskIds.has(task.id))
        .map((task) => unscheduledReason(task, context));
}
/** How far ahead a task counts as planned: the app's upcoming window. */
export const PLANNED_WINDOW_DAYS = 60;
/**
 * The first day each task is planned on, from `today` through the planned
 * window — what "is this task scheduled?" means on the Today screen and to
 * the assistant alike.
 *
 * The assistant used to ask only about the days in the question, so "what's
 * on today" listed work planned for Thursday as unscheduled while the app
 * showed it scheduled.
 */
export function plannedTaskDates(schedules, today, days = PLANNED_WINDOW_DAYS) {
    const end = shiftDateKey(today, days);
    const inWindow = [...schedules]
        .filter((s) => s.date >= today && s.date <= end)
        .sort((a, b) => a.date.localeCompare(b.date));
    const planned = new Map();
    for (const schedule of inWindow) {
        for (const st of schedule.scheduled_tasks ?? []) {
            if (!st.is_buffer && !planned.has(st.task_id))
                planned.set(st.task_id, schedule.date);
        }
    }
    return planned;
}
//# sourceMappingURL=unscheduled.js.map